import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Download, Loader2, Pause, Play, X } from "lucide-react";

import {
  api,
  ApiError,
  CLIP_MAX_DURATION_S,
  DEFAULT_REPLAY_RATE,
  type CameraActivity,
  type ReplayPlay,
  type Scope,
} from "../api";
import { connectHls, type StreamConnection } from "../webrtc";
import {
  connectDirect,
  createStillGrabber,
  directSupported,
  type DirectConnection,
  type StillGrabber,
} from "../direct";
import { FisheyeOverlay, useFisheyeGeometry } from "./FisheyePlayer";
import { InfoHint } from "./InfoHint";
import { useRecordingDownloads } from "./RecordingDownloadsContext";
import { SearchableSelect } from "./SearchableSelect";
import {
  PlayerFullscreenButton,
  playerChromeBtnClass,
} from "./PlayerFullscreenButton";
import { AlertCropImage } from "./weaponAlertsShared";
import { friendlyError } from "../errors";
import { useRole } from "../hooks/useRole";

/**
 * Recordings player: one calendar day of a camera as a zoomable scrubbable
 * strip under a playback window.
 *
 * The bars are an INDICATION of when something was happening, not a list of
 * things to click: an operator looking for the moment nothing was supposed to
 * happen needs to reach a quiet minute exactly as easily as a busy one, so the
 * whole strip is clickable and the density is only there to aim with.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
// Decoded hover stills, newest kept. A sweep across the strip touches many
// buckets and coming back to one should be instant.
const STILL_CACHE_MAX = 48;
const BUCKETS = 288; // five minutes across the day; zoom only crops this
const MIN_SPAN_MS = 2 * 60 * 1000;
const DOWNLOAD_RATES = [1, 2, 4, 8, 16];
const THUMB_BUCKET_MS = 30_000;
const NEAR_ALERT_PX = 24;
const ALERT_BAND_PX = 28;

export function todayValue(): string {
  const now = new Date();
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("-");
}

function windowStart(date: string): Date {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
}

function clockLabel(ms: number): string {
  return new Date(ms).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

function shortDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rest = s % 60;
  return rest ? `${m}m ${rest}s` : `${m}m`;
}

function rangeLengthLabel(seconds: number): string {
  const s = Math.max(0, seconds);
  if (s < 60) return `${Math.round(s)} sec`;
  const min = Math.round((s / 60) * 10) / 10;
  return Number.isInteger(min) ? `${min} min` : `${min.toFixed(1)} min`;
}

type MarkMode = "start" | "end";

function previewThumbAtMs(date: string): number {
  const noon = windowStart(date).getTime() + 12 * 60 * 60 * 1000;
  return Math.min(Date.now() - 15_000, noon);
}

function CameraStill({
  cameraId,
  date,
  onAspect,
}: {
  cameraId: string;
  date: string;
  onAspect?: (aspect: number) => void;
}) {
  // Recorder JPEG, same bitstream family as click-to-play. The live mux
  // snapshot is a different picture (typically 16:9) and must not size the
  // card — that is what made the poster jump when playback started.
  const thumb = api.recordings.thumbUrl(cameraId, previewThumbAtMs(date));
  const live = `/api/frames/live/${encodeURIComponent(cameraId)}`;
  const [src, setSrc] = useState(thumb);
  const [gone, setGone] = useState(false);
  useEffect(() => {
    setSrc(thumb);
    setGone(false);
  }, [cameraId, date, thumb]);
  if (gone) return null;
  const fromRecorder = src.includes("/recordings/thumb");
  return (
    <img
      src={src}
      alt=""
      className="absolute inset-0 h-full w-full object-contain"
      onLoad={(e) => {
        const img = e.currentTarget;
        if (
          fromRecorder &&
          img.naturalWidth > 0 &&
          img.naturalHeight > 0
        ) {
          onAspect?.(img.naturalWidth / img.naturalHeight);
        }
      }}
      onError={() => {
        if (fromRecorder) {
          setSrc(live);
          return;
        }
        setGone(true);
      }}
    />
  );
}

function chipClass(active: boolean, disabled = false) {
  return (
    "inline-flex items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium transition " +
    (disabled
      ? "cursor-not-allowed border-white/10 bg-bf-surface/60 text-bf-text opacity-50"
      : active
        ? "border-bf-magenta/40 bg-bf-magenta/15 text-bf-magenta"
        : "border-white/10 bg-bf-surface/60 text-bf-text hover:border-white/20")
  );
}

function clampView(start: number, span: number, origin: number): { start: number; span: number } {
  const nextSpan = Math.min(DAY_MS, Math.max(MIN_SPAN_MS, span));
  const lo = origin;
  const hi = origin + DAY_MS;
  let nextStart = start;
  if (nextStart < lo) nextStart = lo;
  if (nextStart + nextSpan > hi) nextStart = hi - nextSpan;
  return { start: nextStart, span: nextSpan };
}

function tickStepMs(span: number): number {
  if (span >= 6 * 3600_000) return 3600_000;
  if (span >= 90 * 60_000) return 15 * 60_000;
  if (span >= 20 * 60_000) return 5 * 60_000;
  if (span >= 5 * 60_000) return 60_000;
  return 15_000;
}

function tickLabel(ms: number, span: number): string {
  const d = new Date(ms);
  if (span <= 10 * 60_000) {
    return d.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  }
  if (span <= 6 * 3600_000) {
    return d.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: false,
    });
  }
  return d.toLocaleTimeString([], { hour: "numeric", hour12: false });
}

function spanLabel(span: number): string {
  if (span >= 20 * 3600_000) return "24 h";
  if (span >= 3600_000) {
    const h = Math.round(span / 3600_000);
    return `${h} h`;
  }
  const m = Math.max(1, Math.round(span / 60_000));
  return `${m} min`;
}

function TimelineAlertPreview({
  alertId,
  text,
  tsMs,
}: {
  alertId: string;
  text: string;
  tsMs: number;
}) {
  return (
    <div className="w-44 overflow-hidden rounded-lg border border-white/10 bg-bf-surface shadow-xl">
      <AlertCropImage
        alertId={alertId}
        alt={text || "Alert crop"}
        containerClassName="flex aspect-video w-full items-center justify-center overflow-hidden bg-black/40"
        className="max-h-full max-w-full object-contain"
      />
      <div className="px-2 py-1.5 text-[10px] leading-snug text-bf-text">
        <div className="truncate">{text || "Alert"}</div>
        <div className="text-bf-muted">{clockLabel(tsMs)}</div>
      </div>
    </div>
  );
}

function TimelineThumbPreview({
  tsMs,
  url,
  loading,
}: {
  tsMs: number;
  url?: string;
  loading?: boolean;
}) {
  const [img, setImg] = useState<"loading" | "ok" | "fail">(
    url ? "loading" : "fail",
  );
  useEffect(() => {
    setImg(url ? "loading" : "fail");
  }, [url]);

  const showFrame = Boolean(url) && img !== "fail";
  const showSpin = loading || (Boolean(url) && img === "loading");

  return (
    <div className="w-52 overflow-hidden rounded-lg border border-white/10 bg-bf-surface shadow-xl">
      {showFrame || showSpin ? (
        <div className="relative aspect-video w-full overflow-hidden bg-black/40">
          {url && img !== "fail" ? (
            <img
              src={url}
              alt=""
              className="absolute inset-0 h-full w-full object-contain"
              onLoad={() => setImg("ok")}
              onError={() => setImg("fail")}
            />
          ) : null}
          {showSpin && img !== "ok" ? (
            <div className="absolute inset-0 grid place-items-center">
              <Loader2 size={18} className="animate-spin text-bf-muted" />
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="px-2 py-1.5 text-[10px] leading-snug text-bf-muted">
        {clockLabel(tsMs)}
      </div>
    </div>
  );
}

const PLAYBACK_UNAVAILABLE =
  "This camera has no recorded footage to play back. Only cameras on a " +
  "supported recorder, or simulated cameras, can be replayed.";

export function CameraTimeline({
  cameraId,
  cameraName,
  scope,
  date,
}: {
  cameraId: string;
  cameraName: string;
  scope?: Scope;
  date: string;
}) {
  const { role } = useRole();
  const [hover, setHover] = useState<
    | {
        kind: "alert";
        alertId: string;
        text: string;
        tsMs: number;
        leftPct: number;
      }
    | {
        kind: "thumb";
        tsMs: number;
        leftPct: number;
        url?: string;
        loading?: boolean;
      }
    | null
  >(null);
  const thumbTimerRef = useRef<number | null>(null);
  const thumbPosRef = useRef({ ms: 0, leftPct: 0 });
  const [playAt, setPlayAt] = useState<number | null>(null);
  const [replay, setReplay] = useState<ReplayPlay | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [markMode, setMarkMode] = useState<MarkMode | null>(null);
  const [rangeStart, setRangeStart] = useState<number | null>(null);
  const [rangeEnd, setRangeEnd] = useState<number | null>(null);
  const [downloadRate, setDownloadRate] = useState(DEFAULT_REPLAY_RATE);
  const { jobs, startDownload, cancelDownload: cancelStoredDownload } =
    useRecordingDownloads();
  const [videoPaused, setVideoPaused] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const slotRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mediaAspect, setMediaAspect] = useState<number | null>(null);
  const [slotSize, setSlotSize] = useState({ w: 0, h: 0 });
  const [playerFullscreen, setPlayerFullscreen] = useState(false);
  const liveRef = useRef<StreamConnection | null>(null);
  // Relay path: the recorder's own bitstream decoded in the page. One session
  // and one socket serve every click on the strip, so a seek is a message
  // rather than a connection. HLS stays as the fallback where the browser
  // cannot decode what the recorder sends.
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const directRef = useRef<DirectConnection | null>(null);
  // Hover stills come off a second relay session so grabbing one never disturbs
  // what is playing. Decoded stills are cached per bucket, so sweeping back over
  // the strip costs nothing.
  const stillGrabberRef = useRef<StillGrabber | null>(null);
  const stillCacheRef = useRef<Map<number, string>>(new Map());
  // `useDirect` drives which surface is visible; the ref is what the async
  // opener reads, because a state update from this render is not visible to a
  // closure created in it.
  const [useDirect, setUseDirect] = useState(directSupported());
  // A fisheye's recording is the raw disk: it plays dewarped (a view at its
  // pose, the fisheye itself with look-around), drawn from whichever surface
  // the recording is decoded onto.
  const fisheye = useFisheyeGeometry(cameraId);
  const [surfaces, setSurfaces] = useState<{
    canvas: HTMLCanvasElement | null;
    video: HTMLVideoElement | null;
  }>({ canvas: null, video: null });
  useEffect(() => setSurfaces({ canvas: canvasRef.current, video: videoRef.current }), []);
  const directBlockedRef = useRef(false);
  const [directLive, setDirectLive] = useState(false);
  // Where a resume should pick up: the clicked instant plus how much media has
  // been presented since.
  const resumeAtRef = useRef(0);
  // Single-flight gate. A ref, not the `pending` state: React batches state
  // updates, so six clicks in one tick all read `pending === false` and six
  // NVR replay sessions leave together.
  const busyRef = useRef(false);
  const stripRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef({ start: 0, span: DAY_MS });
  const originRef = useRef(0);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    startView: { start: number; span: number };
    anchorMs: number;
    moved: boolean;
  } | null>(null);

  const from = useMemo(() => windowStart(date), [date]);
  const originMs = from.getTime();
  const [view, setView] = useState({ start: originMs, span: DAY_MS });
  viewRef.current = view;
  originRef.current = originMs;

  useEffect(() => {
    setView({ start: originMs, span: DAY_MS });
  }, [originMs, cameraId]);

  const activity = useQuery<CameraActivity>({
    queryKey: [
      "camera-activity",
      cameraId,
      originMs,
      scope?.store ?? "",
      scope?.profileId ?? "",
    ],
    queryFn: () =>
      api.cameras.activity(
        cameraId,
        from,
        new Date(originMs + DAY_MS),
        BUCKETS,
        scope,
      ),
    // The window keeps filling while it is today; a stale strip would quietly
    // stop showing the last few minutes of activity.
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (thumbTimerRef.current !== null) window.clearTimeout(thumbTimerRef.current);
    thumbTimerRef.current = null;
    setHover(null);
  }, [cameraId, originMs]);

  function scheduleThumb(ms: number, leftPct: number) {
    thumbPosRef.current = { ms, leftPct };
    if (ms > Date.now()) {
      setHover({ kind: "thumb", tsMs: ms, leftPct });
      return;
    }
    const bucketNow = Math.round(ms / THUMB_BUCKET_MS) * THUMB_BUCKET_MS;
    const cached = stillCacheRef.current.get(bucketNow);
    if (cached) {
      // Already decoded this bucket: no fetch, no spinner.
      setHover({ kind: "thumb", tsMs: ms, leftPct, url: cached, loading: false });
      return;
    }
    setHover({ kind: "thumb", tsMs: ms, leftPct, loading: true });
    if (thumbTimerRef.current !== null) window.clearTimeout(thumbTimerRef.current);
    thumbTimerRef.current = window.setTimeout(() => {
      thumbTimerRef.current = null;
      const pos = thumbPosRef.current;
      const bucket = Math.round(pos.ms / THUMB_BUCKET_MS) * THUMB_BUCKET_MS;
      void showStill(bucket, pos);
    }, 120);
  }

  /** Decode one recorded frame for the hover preview.
   *
   * Through the relay this is a keyframe off the wire, which is why it can keep
   * up with a moving cursor. The server-side still is kept as the fallback for
   * cameras the relay cannot serve (simulated sources, unsupported recorders).
   */
  async function showStill(bucket: number, pos: { ms: number; leftPct: number }) {
    const place = (url: string | undefined, loading: boolean) => {
      // A later hover has moved on; do not overwrite it with this answer.
      if (thumbPosRef.current.ms !== pos.ms) return;
      setHover({ kind: "thumb", tsMs: pos.ms, leftPct: pos.leftPct, url, loading });
    };
    const grabber = stillGrabberRef.current;
    if (grabber) {
      const still = await grabber.grab(bucket);
      if (still) {
        const cache = stillCacheRef.current;
        cache.set(bucket, still);
        // A hover sweep can touch a lot of buckets; keep the newest handful.
        if (cache.size > STILL_CACHE_MAX) {
          cache.delete(cache.keys().next().value as number);
        }
        place(still, false);
        return;
      }
    }
    place(api.recordings.thumbUrl(cameraId, bucket), false);
  }

  /** Tear down the HLS fallback only. The relay session is deliberately kept:
   *  reusing its warm socket is what makes a seek cost ~0.1 s. */
  function closeHls() {
    liveRef.current?.close();
    liveRef.current = null;
    const video = videoRef.current;
    if (video) {
      video.removeAttribute("src");
      video.srcObject = null;
      video.load();
    }
  }

  function closeLive() {
    closeHls();
    directRef.current?.close();
    directRef.current = null;
    stillGrabberRef.current?.close();
    stillGrabberRef.current = null;
    stillCacheRef.current.clear();
    setDirectLive(false);
  }

  /** Click-to-pause for the relay path.
   *
   * There is no media element to pause, so pausing ends the stream and
   * resuming re-seeks to where the picture stopped. A reseek costs about as
   * long as a keyframe takes to arrive, which is why this is cheap enough to
   * use as a pause button at all.
   */
  function toggleDirectPause() {
    const direct = directRef.current;
    if (!direct || pending || !directLive) return;
    if (videoPaused) {
      direct.seek(resumeAtRef.current);
      setPlayAt(resumeAtRef.current);
      setVideoPaused(false);
      return;
    }
    resumeAtRef.current = (playAt ?? 0) + direct.mediaTimeMs();
    direct.stop();
    setVideoPaused(true);
  }

  function togglePause() {
    if (useDirect) {
      toggleDirectPause();
      return;
    }
    const video = videoRef.current;
    if (!video || pending) return;
    if (video.paused) void video.play().catch(() => undefined);
    else video.pause();
  }

  /** Open the relay session up front so the first click pays no setup.
   *
   * Returns null when the relay or this browser cannot serve the camera, which
   * sends the caller down the HLS path instead.
   */
  async function ensureDirect(signal: AbortSignal): Promise<DirectConnection | null> {
    if (!directSupported() || directBlockedRef.current || !canvasRef.current) return null;
    if (directRef.current) return directRef.current;
    try {
      const opened = await api.streams.open(
        { camera_id: cameraId, kind: "replay" },
        signal,
      );
      if (signal.aborted) return null;
      if (!stillGrabberRef.current) {
        // Its own session: a hover must not interrupt playback.
        try {
          const forStills = await api.streams.open(
            { camera_id: cameraId, kind: "replay" },
            signal,
          );
          if (!signal.aborted) {
            stillGrabberRef.current = createStillGrabber(forStills.session);
          }
        } catch {
          // Hover previews fall back to the server-side still.
        }
      }
      directRef.current = connectDirect(opened.session, canvasRef.current, {
        onError: (message) => setError(message),
        onFirstFrame: () => {
          setDirectLive(true);
          const c = canvasRef.current;
          if (c && c.width > 0 && c.height > 0) {
            const next = c.width / c.height;
            setMediaAspect((prev) =>
              prev && Math.abs(prev - next) / prev < 0.02 ? prev : next,
            );
          }
        },
        // The recorded window ends at whatever "now" was when the seek was
        // made, so a long watch runs off the end. Show it as paused rather
        // than leaving a live-looking badge over a frozen picture.
        onEnded: () => setVideoPaused(true),
      });
      return directRef.current;
    } catch {
      // Unsupported recorder brand, relay down, or a codec this browser cannot
      // decode. Fall back for the rest of this camera's session.
      directBlockedRef.current = true;
      setUseDirect(false);
      return null;
    }
  }

  useEffect(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    busyRef.current = false;
    setPending(false);
    setReplay(null);
    setError(null);
    setPlayAt(null);
    setMarkMode(null);
    setRangeStart(null);
    setRangeEnd(null);
    closeLive();
    // A different camera may be NVR-backed even if the last one was not, so
    // give the relay another chance rather than staying on the fallback.
    directBlockedRef.current = false;
    setUseDirect(directSupported());
    setMediaAspect(null);
    // Open the relay session now, not on the first click. The socket costs a
    // TLS handshake and leaves TCP in slow start, and a 4K keyframe is most of
    // a megabyte -- paying that on the click made the first seek ~2.1 s where
    // every later one was ~0.5 s.
    const warm = new AbortController();
    void ensureDirect(warm.signal);
    return () => {
      warm.abort();
      abortRef.current?.abort();
      closeLive();
      void api.recordings.stop(cameraId).catch(() => undefined);
    };
  }, [cameraId]);

  useEffect(() => {
    setMarkMode(null);
    setRangeStart(null);
    setRangeEnd(null);
    setError(null);
  }, [originMs]);

  useLayoutEffect(() => {
    const el = slotRef.current;
    if (!el) return;
    const measure = () => setSlotSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const sync = () =>
      setPlayerFullscreen(
        Boolean(playerRef.current && document.fullscreenElement === playerRef.current),
      );
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);

  const preparingDownload = jobs.find(
    (job) => job.cameraId === cameraId && job.status === "preparing",
  );
  const readyDownload = jobs.find(
    (job) =>
      job.cameraId === cameraId &&
      job.status === "ready" &&
      job.clip &&
      job.startMs === rangeStart &&
      job.endMs === rangeEnd,
  );
  const failedDownload = jobs.find(
    (job) =>
      job.cameraId === cameraId &&
      job.status === "failed" &&
      job.startMs === rangeStart &&
      job.endMs === rangeEnd,
  );
  const [downloadNow, setDownloadNow] = useState(Date.now());
  useEffect(() => {
    if (!preparingDownload) return;
    const iv = window.setInterval(() => setDownloadNow(Date.now()), 1000);
    return () => window.clearInterval(iv);
  }, [preparingDownload?.id]);
  const downloadElapsed = preparingDownload
    ? Math.max(0, Math.round((downloadNow - preparingDownload.startedAt) / 1000))
    : 0;

  const detections = activity.data?.detections ?? [];
  const alerts = activity.data?.alerts ?? [];
  const peak = Math.max(1, ...detections);
  // Undefined while the density is still loading (or if it failed). Only an
  // explicit "none" blocks a click: refusing because we have not heard back
  // yet would look exactly like a camera that cannot be replayed at all.
  const playback = activity.data?.playback;

  function cancel() {
    const ac = abortRef.current;
    abortRef.current = null;
    busyRef.current = false;
    setPending(false);
    ac?.abort();
  }

  function stopPlayback() {
    cancel();
    closeHls();
    // Stop the stream but keep the relay socket: it is already warm, so the
    // next click starts in ~0.5 s instead of paying for a new connection.
    directRef.current?.stop();
    setDirectLive(false);
    setVideoPaused(false);
    setReplay(null);
    setPlayAt(null);
    void api.recordings.stop(cameraId).catch(() => undefined);
  }

  function cancelDownload() {
    if (preparingDownload) cancelStoredDownload(preparingDownload.id);
  }

  function clearRange() {
    setMarkMode(null);
    setRangeStart(null);
    setRangeEnd(null);
  }

  function placeMark(mode: MarkMode, rawMs: number) {
    const instantMs = Math.round(rawMs);
    if (playback === "none") {
      setError(PLAYBACK_UNAVAILABLE);
      setMarkMode(null);
      return;
    }
    if (instantMs > Date.now()) {
      setError("That moment hasn't happened yet. Pick a time in the past.");
      return;
    }
    let nextStart = rangeStart;
    let nextEnd = rangeEnd;
    if (mode === "start") nextStart = instantMs;
    else nextEnd = instantMs;
    if (nextStart !== null && nextEnd !== null && nextStart > nextEnd) {
      const swap = nextStart;
      nextStart = nextEnd;
      nextEnd = swap;
    }
    setRangeStart(nextStart);
    setRangeEnd(nextEnd);
    setMarkMode(mode === "start" ? "end" : null);
    setError(null);
    if (
      nextStart !== null &&
      nextEnd !== null &&
      nextEnd - nextStart > CLIP_MAX_DURATION_S * 1000
    ) {
      setError(
        `A download can be at most ${shortDuration(CLIP_MAX_DURATION_S)}. Narrow the range.`,
      );
    }
  }

  function onStripInstant(rawMs: number) {
    if (markMode) {
      placeMark(markMode, rawMs);
      return;
    }
    void playFrom(rawMs);
  }

  async function downloadRange() {
    if (rangeStart === null || rangeEnd === null) return;
    if (rangeEnd <= rangeStart) {
      setError("Set the start before the end.");
      return;
    }
    const durationS = Math.round((rangeEnd - rangeStart) / 1000);
    if (durationS < 1) {
      setError("Pick two different times.");
      return;
    }
    if (durationS > CLIP_MAX_DURATION_S) {
      setError(
        `A download can be at most ${shortDuration(CLIP_MAX_DURATION_S)}. Narrow the range.`,
      );
      return;
    }
    if (playback === "none") {
      setError(PLAYBACK_UNAVAILABLE);
      return;
    }
    setError(null);
    startDownload({
      cameraId,
      cameraName,
      startMs: rangeStart,
      endMs: rangeEnd,
      rate: downloadRate,
      thumbUrl: api.recordings.thumbUrl(cameraId, rangeStart),
    });
  }

  async function playFrom(rawMs: number) {
    // A click lands at a fractional millisecond; the api takes epoch ms as an
    // integer and rejects anything else, so every click failed validation.
    const instantMs = Math.round(rawMs);
    if (playback === "none") {
      setError(PLAYBACK_UNAVAILABLE);
      return;
    }
    // Half of today's strip is usually still in the future; asking a recorder
    // for it returns the same "no footage" as a channel that never recorded,
    // which sends the operator looking at the recorder for nothing.
    if (instantMs > Date.now()) {
      setError("That moment hasn't happened yet. Pick a time in the past.");
      return;
    }
    // A new click replaces the current replay session. Aborting the in-flight
    // request is what keeps NVR replay sessions from stacking.
    if (busyRef.current) cancel();
    closeHls();
    const ac = new AbortController();
    abortRef.current = ac;
    busyRef.current = true;
    setPlayAt(instantMs);
    setPending(true);
    setVideoPaused(false);
    setError(null);
    setReplay(null);
    try {
      const direct = await ensureDirect(ac.signal);
      if (ac.signal.aborted) return;
      if (direct) {
        direct.seek(instantMs);
        return;
      }
      const video = videoRef.current;
      if (!video) return;
      const result = await api.recordings.play(
        {
          camera_id: cameraId,
          start_ms: instantMs,
        },
        ac.signal,
      );
      if (ac.signal.aborted) return;
      liveRef.current = connectHls(result.path, video, { replay: true });
      setReplay(result);
    } catch (err) {
      if (ac.signal.aborted) return;
      setError(
        err instanceof ApiError
          ? friendlyError(err, role, "Could not play that moment.")
          : "Could not play that moment.",
      );
    } finally {
      // Keyed on being the CURRENT request, not on the abort flag: cancelling
      // sets that flag, so the old code left the button stuck on "Cancel"
      // forever and there was no way back.
      if (abortRef.current === ac) {
        abortRef.current = null;
        busyRef.current = false;
        setPending(false);
      }
    }
  }

  const now = Date.now();
  const zoomed = view.span < DAY_MS - 1;

  function msAtClientX(clientX: number): number {
    const rect = stripRef.current?.getBoundingClientRect();
    const { start, span } = viewRef.current;
    if (!rect || rect.width <= 0) return start;
    const f = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return start + f * span;
  }

  function applyView(start: number, span: number) {
    setView(clampView(start, span, originRef.current));
  }

  function zoomAt(anchorMs: number, factor: number) {
    const { start, span } = viewRef.current;
    if (factor < 1 && span <= MIN_SPAN_MS) return;
    if (factor > 1 && span >= DAY_MS) return;
    const nextSpan = Math.min(DAY_MS, Math.max(MIN_SPAN_MS, span * factor));
    const f = span <= 0 ? 0.5 : (anchorMs - start) / span;
    setView(clampView(anchorMs - f * nextSpan, nextSpan, originRef.current));
  }

  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomAt(msAtClientX(e.clientX), e.deltaY > 0 ? 1.2 : 1 / 1.2);
    };
    const onMouseDown = (e: MouseEvent) => {
      // Chrome starts autoscroll on middle-mousedown unless this is canceled.
      if (e.button === 1) e.preventDefault();
    };
    const onAux = (e: MouseEvent) => {
      if (e.button === 1) e.preventDefault();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("mousedown", onMouseDown);
    el.addEventListener("auxclick", onAux);
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("mousedown", onMouseDown);
      el.removeEventListener("auxclick", onAux);
    };
  }, []);

  const nowFraction = (now - view.start) / view.span;
  const playFraction = playAt === null ? null : (playAt - view.start) / view.span;
  const startFraction =
    rangeStart === null ? null : (rangeStart - view.start) / view.span;
  const endFraction =
    rangeEnd === null ? null : (rangeEnd - view.start) / view.span;
  const rangeLo = rangeStart === null || rangeEnd === null
    ? null
    : Math.min(rangeStart, rangeEnd);
  const rangeHi = rangeStart === null || rangeEnd === null
    ? null
    : Math.max(rangeStart, rangeEnd);
  const rangeLeft =
    rangeLo === null ? null : (rangeLo - view.start) / view.span;
  const rangeRight =
    rangeHi === null ? null : (rangeHi - view.start) / view.span;
  const rangeDurationS =
    rangeLo !== null && rangeHi !== null
      ? Math.max(0, Math.round((rangeHi - rangeLo) / 1000))
      : 0;
  const rangeReady =
    rangeLo !== null &&
    rangeHi !== null &&
    rangeDurationS >= 1 &&
    rangeDurationS <= CLIP_MAX_DURATION_S;
  // Pin bars to the same local origin the zoom viewport uses. The API's
  // from_ms can disagree by a timezone parse, which is harmless on a full
  // day (bars just shift) and empty once you zoom into the gap.
  const dataFrom = originMs;
  const dataBucket = DAY_MS / Math.max(detections.length, BUCKETS);
  const ticks = useMemo(() => {
    const step = tickStepMs(view.span);
    const first = originMs + Math.ceil((view.start - originMs) / step) * step;
    const out: number[] = [];
    for (let t = first; t < view.start + view.span; t += step) out.push(t);
    return out;
  }, [view.start, view.span, originMs]);

  // Whichever transport is serving this camera, the player is either showing a
  // moment or it is idle. Everything overlaid on the picture keys off this, so
  // a new transport cannot leave the idle still and hint on top of live video.
  const playing = directLive || Boolean(replay);
  const cardStyle =
    playerFullscreen || !mediaAspect || slotSize.w < 1 || slotSize.h < 1
      ? { width: "100%", height: "100%" }
      : {
          width: Math.min(slotSize.w, slotSize.h * mediaAspect),
          height: Math.min(slotSize.h, slotSize.w / mediaAspect),
        };

  const playerHint =
    playback === "none"
      ? PLAYBACK_UNAVAILABLE
      : markMode === "start"
        ? "Click the timeline to set the download start."
        : markMode === "end"
          ? "Click the timeline to set the download end."
          : "Click the timeline to play a moment.";

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div
        ref={slotRef}
        className="flex min-h-[12rem] min-w-0 flex-1 items-center justify-center"
      >
        <div
          ref={playerRef}
          className="relative overflow-hidden rounded-lg bg-bf-surface [:fullscreen]:h-full [:fullscreen]:w-full [:fullscreen]:rounded-none [:fullscreen]:bg-black"
          style={cardStyle}
        >
          {/* Both surfaces stay mounted so their refs exist before the first
              click decides which transport serves this camera. The canvas
              bitmap is the camera's own pixels; the card is sized to that
              picture so the surface does not outrun the frame. */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
            <canvas
              ref={canvasRef}
              className={
                "pointer-events-auto block min-h-0 min-w-0 max-h-full max-w-full " +
                (useDirect ? (playing && !pending && !fisheye ? "cursor-pointer" : "invisible") : "hidden")
              }
              onClick={togglePause}
            />
          </div>
          <video
            ref={videoRef}
            muted
            autoPlay
            playsInline
            className={
              "bf-replay-video h-full w-full object-contain " +
              (useDirect ? "hidden" : playing && !pending && !fisheye ? "cursor-pointer" : "invisible")
            }
            onPlay={() => setVideoPaused(false)}
            onPause={() => setVideoPaused(true)}
            onLoadedMetadata={(e) => {
              const v = e.currentTarget;
              if (v.videoWidth > 0 && v.videoHeight > 0) {
                const next = v.videoWidth / v.videoHeight;
                setMediaAspect((prev) =>
                  prev && Math.abs(prev - next) / prev < 0.02 ? prev : next,
                );
              }
            }}
            onClick={togglePause}
          />
          {fisheye && playing && !pending && (
            <FisheyeOverlay
              geometry={fisheye}
              cameraId={cameraId}
              canvas={useDirect ? surfaces.canvas : null}
              video={useDirect ? null : surfaces.video}
              layoutsAt="right-3 top-14"
            />
          )}
          {playing && !pending && playAt !== null && (
            <div className="pointer-events-none absolute right-3 top-3 rounded-lg border border-white/10 bg-black/70 px-3 py-1.5 text-sm font-medium text-bf-text">
              started at {clockLabel(playAt)}
            </div>
          )}
          {playing && !pending && (
            <button
              type="button"
              className={"absolute bottom-2 left-2 z-10 " + playerChromeBtnClass}
              onClick={togglePause}
              aria-label={videoPaused ? "Play" : "Pause"}
              title={videoPaused ? "Play" : "Pause"}
            >
              {videoPaused ? (
                <Play size={16} fill="currentColor" />
              ) : (
                <Pause size={16} fill="currentColor" />
              )}
            </button>
          )}
          <PlayerFullscreenButton targetRef={playerRef} />
          {pending && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 size={28} className="animate-spin text-white/80" />
            </div>
          )}
          {!pending && !playing && (
            <>
              <CameraStill
                cameraId={cameraId}
                date={date}
                onAspect={setMediaAspect}
              />
              <div className="pointer-events-none absolute inset-0 flex items-end justify-center px-6 pb-5">
                <p className="rounded-lg border border-white/10 bg-black/70 px-3 py-2 text-center text-sm font-medium text-bf-text">
                  {playerHint}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex w-full shrink-0 flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-bf-muted">Timeline</span>
          <span className="text-sm text-bf-muted">{spanLabel(view.span)}</span>
          {zoomed && (
            <button
              type="button"
              onClick={() => applyView(originMs, DAY_MS)}
              className={chipClass(false)}
              title="Show the full day"
            >
              reset
            </button>
          )}
          <span
            className="ml-4 inline-flex items-center gap-1.5 text-sm text-bf-muted"
            title="Hold the middle mouse button and drag up or down to zoom"
          >
            <span className="font-medium tracking-tight" aria-hidden>
              {"<>"}
            </span>
            middle mouse to zoom
          </span>
          <InfoHint>
            A full day of {cameraName}. Scroll or hold the middle mouse button
            and drag (up/down zooms, left/right pans). Hover the strip for a
            frame. Hover a triangle to see the crop. Click the strip to play.
          </InfoHint>
          {activity.isFetching && (
            <Loader2 size={16} className="animate-spin text-bf-muted" />
          )}
          {playback === "none" && (
            <span className="text-sm text-amber-300/80">Playback unavailable</span>
          )}
          {activity.isError && (
            <span className="text-sm text-amber-300/80">
              Activity unavailable — the timeline is still clickable
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3">
          <span className="text-sm font-medium text-bf-muted">specify</span>
          <button
            type="button"
            className={chipClass(markMode === "start", playback === "none")}
            aria-pressed={markMode === "start"}
            aria-label="Set download start, then click the timeline"
            disabled={playback === "none"}
            onClick={() =>
              setMarkMode((mode) => (mode === "start" ? null : "start"))
            }
          >
            Start
          </button>
          <span className="text-sm text-bf-muted">–</span>
          <button
            type="button"
            className={chipClass(markMode === "end", playback === "none")}
            aria-pressed={markMode === "end"}
            aria-label="Set download end, then click the timeline"
            disabled={playback === "none"}
            onClick={() =>
              setMarkMode((mode) => (mode === "end" ? null : "end"))
            }
          >
            End
          </button>
          <span className="text-sm text-bf-muted">to</span>
          <button
            type="button"
            className={chipClass(false, !rangeReady || Boolean(preparingDownload) || playback === "none")}
            disabled={!rangeReady || Boolean(preparingDownload) || playback === "none"}
            onClick={() => void downloadRange()}
          >
            <span className="inline-flex items-center gap-1.5">
              <Download size={16} /> Download
            </span>
          </button>
          <span className="text-sm font-medium text-bf-muted">@</span>
          <SearchableSelect
            className="w-24"
            ariaLabel="Download pull speed"
            value={String(downloadRate)}
            onChange={(v) => setDownloadRate(Number(v))}
            searchThreshold={99}
            options={DOWNLOAD_RATES.map((rate) => ({
              value: String(rate),
              label: `${rate}x`,
            }))}
          />
          {rangeReady && (
            <span className="text-sm text-bf-muted">
              {rangeLengthLabel(rangeDurationS)}
            </span>
          )}
          {preparingDownload && (
            <button
              type="button"
              className={chipClass(false)}
              onClick={cancelDownload}
            >
              <span className="inline-flex items-center gap-1.5">
                <Loader2 size={16} className="animate-spin" />
                Preparing ({downloadElapsed}s)
                <X size={16} />
              </span>
            </button>
          )}
          {readyDownload?.clip && (
            <a
              href={readyDownload.clip.url}
              download={readyDownload.clip.filename}
              className={chipClass(true)}
            >
              <span className="inline-flex items-center gap-1.5">
                <Download size={16} /> Save clip
              </span>
            </a>
          )}
          {(rangeStart !== null || rangeEnd !== null) && (
            <button
              type="button"
              className={chipClass(false)}
              aria-label="Clear download range"
              onClick={clearRange}
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      <div className="relative shrink-0">
        {hover && (
          <div
            className="pointer-events-none absolute bottom-full z-20 mb-1"
            style={{
              left: `${hover.leftPct}%`,
              transform:
                hover.leftPct < 12
                  ? undefined
                  : hover.leftPct > 88
                    ? "translateX(-100%)"
                    : "translateX(-50%)",
            }}
          >
            {hover.kind === "alert" ? (
              <TimelineAlertPreview
                alertId={hover.alertId}
                text={hover.text}
                tsMs={hover.tsMs}
              />
            ) : (
              <TimelineThumbPreview
                tsMs={hover.tsMs}
                url={hover.url}
                loading={hover.loading}
              />
            )}
          </div>
        )}
        <div
          ref={stripRef}
          role="slider"
          tabIndex={0}
          aria-label={
            markMode === "start"
              ? `Set download start for ${cameraName}`
              : markMode === "end"
                ? `Set download end for ${cameraName}`
                : `Play ${cameraName} from a time`
          }
          aria-valuemin={view.start}
          aria-valuemax={view.start + view.span}
          aria-valuenow={playAt ?? view.start}
          aria-valuetext={playAt === null ? "no moment chosen" : clockLabel(playAt)}
          className={
            "relative h-16 select-none overflow-hidden rounded-lg border border-white/10 bg-black/30 " +
            (markMode
              ? "cursor-crosshair"
              : pending
                ? "cursor-wait opacity-70"
                : "cursor-pointer")
          }
          onPointerDown={(e) => {
            if (e.button !== 1) return;
            e.preventDefault();
            e.currentTarget.setPointerCapture(e.pointerId);
            dragRef.current = {
              pointerId: e.pointerId,
              startX: e.clientX,
              startY: e.clientY,
              startView: { ...viewRef.current },
              anchorMs: msAtClientX(e.clientX),
              moved: false,
            };
          }}
          onPointerMove={(e) => {
            const drag = dragRef.current;
            if (!drag || e.pointerId !== drag.pointerId) return;
            const rect = stripRef.current?.getBoundingClientRect();
            if (!rect || rect.width <= 0) return;
            const dx = e.clientX - drag.startX;
            const dy = e.clientY - drag.startY;
            if (Math.abs(dx) + Math.abs(dy) > 3) drag.moved = true;
            if (Math.abs(dy) >= Math.abs(dx)) {
              const factor = Math.pow(1.012, dy);
              const nextSpan = Math.min(
                DAY_MS,
                Math.max(MIN_SPAN_MS, drag.startView.span * factor),
              );
              const f =
                drag.startView.span <= 0
                  ? 0.5
                  : (drag.anchorMs - drag.startView.start) / drag.startView.span;
              applyView(drag.anchorMs - f * nextSpan, nextSpan);
            } else {
              applyView(drag.startView.start - (dx / rect.width) * drag.startView.span, drag.startView.span);
            }
          }}
          onPointerUp={(e) => {
            const drag = dragRef.current;
            if (!drag || e.pointerId !== drag.pointerId) return;
            dragRef.current = null;
            if (!drag.moved) zoomAt(drag.anchorMs, 1 / 1.6);
          }}
          onPointerCancel={() => {
            dragRef.current = null;
          }}
          onMouseMove={(e) => {
            if (dragRef.current) return;
            const rect = stripRef.current?.getBoundingClientRect();
            if (!rect || rect.width <= 0) return;
            let near: (typeof alerts)[number] | null = null;
            let nearDx = NEAR_ALERT_PX;
            if (e.clientY - rect.top <= ALERT_BAND_PX) {
              for (const alert of alerts) {
                const af = (alert.ts_ms - view.start) / view.span;
                if (af < 0 || af > 1) continue;
                const dx = Math.abs(e.clientX - (rect.left + af * rect.width));
                if (dx < nearDx) {
                  nearDx = dx;
                  near = alert;
                }
              }
            }
            if (near) {
              if (thumbTimerRef.current !== null) {
                window.clearTimeout(thumbTimerRef.current);
                thumbTimerRef.current = null;
              }
              const leftPct = ((near.ts_ms - view.start) / view.span) * 100;
              setHover({
                kind: "alert",
                alertId: near.alert_id || `${near.ts_ms}`,
                text: near.text || (near.kind === "policy" ? "Policy" : "Alert"),
                tsMs: near.ts_ms,
                leftPct,
              });
              return;
            }
            const leftPct = ((e.clientX - rect.left) / rect.width) * 100;
            scheduleThumb(Math.round(msAtClientX(e.clientX)), leftPct);
          }}
          onMouseLeave={() => {
            if (thumbTimerRef.current !== null) {
              window.clearTimeout(thumbTimerRef.current);
              thumbTimerRef.current = null;
            }
            setHover(null);
          }}
          onClick={(e) => {
            if (e.button !== 0) return;
            onStripInstant(msAtClientX(e.clientX));
          }}
          onKeyDown={(e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            onStripInstant(playAt ?? view.start);
          }}
        >
          {ticks.map((t) => (
            <div
              key={t}
              className="absolute top-0 h-full border-l border-white/5"
              style={{ left: `${((t - view.start) / view.span) * 100}%` }}
            />
          ))}

          <div className="absolute inset-x-0 bottom-0 h-11">
            {detections.map((count, i) => {
              if (count === 0) return null;
              const bucketStart = dataFrom + i * dataBucket;
              const left = ((bucketStart - view.start) / view.span) * 100;
              const width = (dataBucket / view.span) * 100;
              if (left + width < 0 || left > 100) return null;
              return (
                <div
                  key={i}
                  className="absolute bottom-0 bg-bf-violet/70"
                  style={{
                    left: `${left}%`,
                    width: `${Math.max(width, 0.15)}%`,
                    height: `${12 + (count / peak) * 88}%`,
                  }}
                />
              );
            })}
          </div>

          {alerts.map((alert, i) => {
            const f = (alert.ts_ms - view.start) / view.span;
            if (f < 0 || f > 1) return null;
            const policy = alert.kind === "policy";
            const label = alert.text || (policy ? "Policy" : "Alert");
            const lit =
              hover?.kind === "alert" &&
              hover.alertId === (alert.alert_id || `${alert.ts_ms}`);
            return (
              <button
                key={`${alert.alert_id || alert.ts_ms}-${i}`}
                type="button"
                className={
                  "absolute top-0 z-10 flex h-7 w-11 -translate-x-1/2 items-start justify-center pt-0.5 transition " +
                  (policy ? "text-amber-300" : "text-red-400") +
                  (lit
                    ? " z-20 scale-125 drop-shadow-[0_0_10px_currentColor]"
                    : "")
                }
                style={{ left: `${f * 100}%` }}
                aria-label={`${label} at ${clockLabel(alert.ts_ms)}.`}
                onFocus={() => {
                  setHover({
                    kind: "alert",
                    alertId: alert.alert_id || `${alert.ts_ms}`,
                    text: label,
                    tsMs: alert.ts_ms,
                    leftPct: f * 100,
                  });
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onStripInstant(alert.ts_ms);
                }}
              >
                <AlertTriangle size={lit ? 18 : 14} />
              </button>
            );
          })}

          {rangeLeft !== null && rangeRight !== null && (
            <div
              className="pointer-events-none absolute top-0 z-[4] h-full bg-bf-violet/25"
              style={{
                left: `${Math.min(1, Math.max(0, rangeLeft)) * 100}%`,
                width: `${Math.min(1, Math.max(0, rangeRight) - Math.min(1, Math.max(0, rangeLeft))) * 100}%`,
              }}
            />
          )}
          {nowFraction >= 0 && nowFraction <= 1 && (
            <div
              className="absolute top-0 h-full w-px bg-white/40"
              style={{ left: `${nowFraction * 100}%` }}
              title="Now"
            />
          )}
          {startFraction !== null && startFraction >= 0 && startFraction <= 1 && (
            <div
              className="absolute top-0 z-[6] h-full w-0.5 bg-emerald-400"
              style={{ left: `${startFraction * 100}%` }}
              title={`Start ${clockLabel(rangeStart!)}`}
            />
          )}
          {endFraction !== null && endFraction >= 0 && endFraction <= 1 && (
            <div
              className="absolute top-0 z-[6] h-full w-0.5 bg-amber-300"
              style={{ left: `${endFraction * 100}%` }}
              title={`End ${clockLabel(rangeEnd!)}`}
            />
          )}
          {playFraction !== null && playFraction >= 0 && playFraction <= 1 && (
            <div
              className="absolute top-0 z-[5] h-full w-0.5 bg-bf-magenta"
              style={{ left: `${playFraction * 100}%` }}
            />
          )}
        </div>

        <div className="relative mt-1 h-4 text-[10px] text-bf-muted">
          {ticks.map((t) => {
            const pct = ((t - view.start) / view.span) * 100;
            return (
              <span
                key={t}
                className="absolute"
                style={{
                  left: `${pct}%`,
                  transform:
                    pct < 1
                      ? undefined
                      : pct > 99
                        ? "translateX(-100%)"
                        : "translateX(-50%)",
                }}
              >
                {tickLabel(t, view.span)}
              </span>
            );
          })}
        </div>
      </div>

      {activity.data?.truncated && (
        <p className="text-sm text-bf-muted">
          Very busy period — the bars show a sample, not every detection.
        </p>
      )}
      {(error || failedDownload?.error) && (
        <p className="text-sm text-red-300">{error || failedDownload?.error}</p>
      )}
      {replay && playAt !== null && !pending && replay.simulated && (
        <p className="text-sm text-bf-muted">simulated recording</p>
      )}
    </div>
  );
}
